import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { SITE_URL } from '@/components/feature/SEOHead';

const teamMembers = [
  {
    name: 'Cameron Thompson',
    role: 'Agency Principal',
    img: 'https://storage.readdy-site.link/project_files/5a4bdf41-1aeb-4fa9-8af1-697cd10aa572/f860084d-aa32-4e78-b420-78d05d635e90_Pro-Headshot.jpg?v=6dc905d8bbb428efd20b66921b14dbb9',
    bio: 'Cameron founded the agency with a commitment to honest, personalized insurance advice. He is dedicated to finding the best coverage for every client.',
    imgPos: '50% 8%',
    bgSize: '145%',
  },
  {
    name: 'Kellie Thompson',
    role: 'Agency Partner',
    img: 'https://storage.readdy-site.link/project_files/5a4bdf41-1aeb-4fa9-8af1-697cd10aa572/acf78d08-8e54-4ff6-918e-76e7209ba9a8_Kellie.jpg?v=72be9876fc7508a3e3f3affad0259cb6',
    bio: 'Kellie helped found the agency in 2022 and has been an irreplaceable asset to the agency.',
    imgPos: '50% top',
  },
  {
    name: 'Holley Adams',
    role: 'Agency Manager',
    img: 'https://public.readdy.ai/ai/img_res/edited_7d53cd0e97f2f4aa33218e63e5803657_7db5d7cc.jpg',
    bio: 'Holley is our swiss army knife. Whether business or personal insurance, Holley is here to help find exactly what you need.',
    imgPos: '50% top',
  },
];

const reviews = [
  { text: '"Previously, I didn\'t really have any idea who my agent was but Cameron took the time to make sure I knew how to reach him and made me feel like I was part of his family."', author: 'Carey C.', rating: 5 },
  { text: '"The White Oak team were fantastic to work with! They were quick to respond and were able to get me set up with exactly what I needed for my business!"', author: 'Ryan H.', rating: 5 },
  { text: '"Working with Cameron was a great experience! He clearly explained my existing policy and showed me where I could do better."', author: 'Jackie A.', rating: 5 },
  { text: '"I have been using this agency for years. They always find me great rates and the customer service is top-notch. Highly recommend!"', author: 'Mark T.', rating: 5 },
  { text: '"The team was incredibly helpful when I had to file a claim. They made a stressful situation much more manageable."', author: 'Dana L.', rating: 5 },
  { text: '"Switched to this agency two years ago and couldn\'t be happier. They really take the time to understand your needs."', author: 'Brian K.', rating: 5 },
];

const advantages = [
  { icon: 'ri-shield-check-line', title: 'Wide Variety of Solutions', desc: 'As an independent agency, we work with many carriers to find you the best options.' },
  { icon: 'ri-group-line', title: 'There When You Need Us', desc: 'Our team is always available to answer questions and support you in your time of need.' },
  { icon: 'ri-route-line', title: 'With You Every Step', desc: 'From quoting to claims, we\'re by your side throughout your entire insurance journey.' },
  { icon: 'ri-award-line', title: 'Licensed Advisors', desc: 'We are licensed insurance advisors committed to your financial protection.' },
];

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About White Oak Insurance Group',
    description:
      'Learn about White Oak Insurance Group, an independent insurance agency in Hattiesburg, MS founded by Cameron Thompson with a commitment to honest, personalized insurance advice.',
    url: `${SITE_URL}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'White Oak Insurance Group',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'White Oak Insurance Group',
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.ico`,
    description:
      'An independent insurance agency in Hattiesburg, MS dedicated to protecting what matters most to you.',
    telephone: '+1-601-325-9000',
    email: 'cameron@whiteoakig.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '406 Hemphill St',
      addressLocality: 'Hattiesburg',
      addressRegion: 'MS',
      postalCode: '39401',
      addressCountry: 'US',
    },
    foundingDate: '2022',
    employee: [
      {
        '@type': 'Person',
        name: 'Cameron Thompson',
        jobTitle: 'Agency Principal',
      },
      {
        '@type': 'Person',
        name: 'Kellie Thompson',
        jobTitle: 'Agency Partner',
      },
      {
        '@type': 'Person',
        name: 'Holley Adams',
        jobTitle: 'Agency Manager',
      },
    ],
    areaServed: {
      '@type': 'State',
      name: 'Mississippi',
    },
    sameAs: ['https://www.facebook.com', 'https://www.linkedin.com'],
  },
];

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="About Us | White Oak Insurance Group – Hattiesburg, MS"
        description="Meet the team at White Oak Insurance Group, an independent insurance agency in Hattiesburg, MS. Founded by Cameron Thompson with a mission to provide honest, personalized insurance advice."
        keywords="about White Oak Insurance Group, Cameron Thompson insurance, independent insurance agency Mississippi, Hattiesburg insurance agency team"
        canonical="/about"
        ogImage="https://storage.readdy-site.link/project_files/5a4bdf41-1aeb-4fa9-8af1-697cd10aa572/f860084d-aa32-4e78-b420-78d05d635e90_Pro-Headshot.jpg"
        jsonLd={aboutJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative h-[500px] flex items-end justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-no-repeat bg-[#0b2240]"
          style={{
            backgroundImage:
              'url(https://storage.readdy-site.link/project_files/5a4bdf41-1aeb-4fa9-8af1-697cd10aa572/f2a9b94d-1bf0-4975-9c5a-496ed605e14b_Family.jpg?v=220b211723fceea6fa5578666a778480)',
            backgroundSize: '100% auto',
            backgroundPosition: 'center 20%',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/70" />
        <div className="relative z-10 w-full text-center px-6 pb-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 whitespace-nowrap">About White Oak Insurance Group</h1>
            <div className="w-16 h-1 bg-[#9cbde5] mx-auto mb-5" />
            <p className="text-white/80 text-lg">
              An independent agency dedicated to protecting what matters most to you.
            </p>
          </div>
        </div>
      </section>

      <main>
        {/* Agency Section */}
        <section id="agency" className="py-20">
          <div className="max-w-screen-xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-[#3072b7] font-bold text-sm uppercase tracking-widest">Our Story</span>
              <h2 className="text-3xl font-bold text-[#0b2240] mt-2 mb-5">White Oak Insurance Group</h2>
              <div className="w-12 h-0.5 bg-[#3072b7] mb-6" />
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Effective insurance begins with specialists who approach your needs uniquely—professionals who comprehend your industry and recognize what's at stake for you. We leverage our expertise and experience to evaluate coverage benefits and premium costs from various carriers, providing you with the most suitable options.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                We are committed to our clients' financial security and quality of life, offering dedicated service, knowledgeable advice, and personalized attention from a reliable partner.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                We are licensed in Mississippi.
              </p>
              <Link
                to="/contact"
                className="bg-[#3072b7] hover:bg-[#0b2240] text-white font-bold text-sm px-8 py-3.5 rounded-md transition-colors whitespace-nowrap"
              >
                Get in Touch
              </Link>
            </div>
            <div className="rounded-xl overflow-hidden h-72 lg:h-96">
              <img
                src="https://b3533217.smushcdn.com/3533217/wp-content/uploads/2024/12/Read-Our-Reviews-Cheerful-Young-Married-Couple-Spending-Time-Together-on-the-Sofa-While-Using-a-Tablet.jpg?lossy=2&strip=1&webp=1"
                alt="Agency"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Meet the Team */}
        <section id="team" className="py-20 bg-[#f8f9fc]">
          <div className="max-w-screen-xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-[#3072b7] font-bold text-sm uppercase tracking-widest">Our People</span>
              <h2 className="text-3xl font-bold text-[#0b2240] mt-2 mb-2">Meet Our Team</h2>
              <div className="w-12 h-0.5 bg-[#3072b7] mx-auto" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <div style={{ height: '288px', position: 'relative', overflow: 'hidden' }}>
                    <div style={{
                      position: 'absolute',
                      top: member.cssRotate ? '-15%' : '0',
                      left: '0',
                      right: '0',
                      bottom: member.cssRotate ? undefined : '0',
                      width: '100%',
                      height: member.cssRotate ? '130%' : '100%',
                      backgroundImage: `url(${member.img})`,
                      backgroundSize: (member as { bgSize?: string }).bgSize ?? 'cover',
                      backgroundPosition: member.cssRotate ? 'center center' : (member.imgPos ?? 'center'),
                      transform: member.cssRotate ?? 'none',
                      transformOrigin: 'center center',
                    }} />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-[#0b2240] text-lg">{member.name}</h3>
                    <p className="text-[#3072b7] text-sm font-semibold mb-3">{member.role}</p>
                    <div className="w-10 h-0.5 bg-[#9cbde5] mb-3" />
                    <p className="text-gray-500 text-sm leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Independent Advantage */}
        <section id="independent" className="py-20">
          <div className="max-w-screen-xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-[#3072b7] font-bold text-sm uppercase tracking-widest">Why Independent</span>
              <h2 className="text-3xl font-bold text-[#0b2240] mt-2 mb-2">The Independent Agency Advantage</h2>
              <div className="w-12 h-0.5 bg-[#3072b7] mx-auto mb-4" />
              <p className="text-gray-500 text-sm max-w-lg mx-auto">
                Unlike captive agents who represent a single carrier, we shop multiple companies to find you the best coverage at the best price.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((adv, i) => (
                <div
                  key={adv.title}
                  className={`rounded-xl p-8 flex flex-col items-center text-center gap-4 ${
                    i % 2 === 0 ? 'bg-[#0b2240] text-white' : 'bg-white border-2 border-[#9cbde5]'
                  }`}
                >
                  <div className={`w-12 h-12 flex items-center justify-center ${i % 2 === 0 ? 'text-[#9cbde5]' : 'text-[#3072b7]'}`}>
                    <i className={`${adv.icon} text-3xl`} />
                  </div>
                  <h3 className={`font-bold text-base ${i % 2 === 0 ? 'text-white' : 'text-[#0b2240]'}`}>{adv.title}</h3>
                  <p className={`text-sm leading-relaxed ${i % 2 === 0 ? 'text-white/70' : 'text-gray-500'}`}>{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section id="reviews" className="py-20 bg-[#f8f9fc]">
          <div className="max-w-screen-xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-[#3072b7] font-bold text-sm uppercase tracking-widest">Testimonials</span>
              <h2 className="text-3xl font-bold text-[#0b2240] mt-2 mb-2">What Our Clients Say</h2>
              <div className="w-12 h-0.5 bg-[#3072b7] mx-auto" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div key={review.author} className="bg-white rounded-xl p-7 shadow-sm border border-gray-100 flex flex-col gap-4">
                  <div className="flex gap-0.5 text-yellow-400">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed italic flex-1">{review.text}</p>
                  <p className="text-[#0b2240] font-bold text-sm">— {review.author}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <a
                href="https://www.google.com/search?sca_esv=2f50148802d04860&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOfqS_MtDPP3d7miAPBFm_9hXMEFTr6mmAIuLpnGWQOkCS4hj4wkpNNEgrJ4o3egtMu64KMxOPTY8TqBzoH4h2wy_oB3lFxOivlyfJWpAUzireW_T3Q%3D%3D&q=White+Oak+Insurance+Group+LLC+Reviews&sa=X&ved=2ahUKEwio2YaTks2TAxULl2oFHe-UBGgQ0bkNegQINBAF&biw=1912&bih=914#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#3072b7] hover:bg-[#0b2240] text-white font-bold text-sm px-8 py-3.5 rounded-md transition-colors whitespace-nowrap"
              >
                <i className="ri-google-line" /> Read Our Google Reviews
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
